<template>
  <!-- Desktop: full app bar with logo, nav items, user menu -->
  <v-app-bar v-if="display.isDesktop" flat class="desktop-app-bar" app height="68">
    <div class="bar-layout">
      <!-- Logo (left) -->
      <div class="bar-logo">
        <v-img
          src="@/assets/images/logo_schaden_white.png"
          width="130px"
          @click="goToHome"
          style="cursor: pointer"
        />
      </div>

      <!-- Kundenfeedback 15.10.2026: zentraler Einstieg in Mail- und
           Benachrichtigungs-Einstellungen direkt am Logo. -->
      <div class="bar-notify">
        <NotifyCenterButton show-label />
      </div>

      <!-- Absprung zur Connection-Plattform (Willkommensseite): Raster-Icon
           mit dauerhaft sichtbarer zweizeiliger Beschriftung, rechts neben
           dem Logo. Unter 1100px Fensterbreite weicht der Text dem Platz
           (Icon + Tooltip bleiben). -->
      <v-tooltip :text="$gt('Zurück zu KARS-Connection')" location="bottom">
        <template #activator="{ props }">
          <button
            v-bind="props"
            type="button"
            class="connection-launcher"
            :aria-label="$gt('Zurück zu KARS-Connection')"
            @click="goToConnection"
          >
            <component :is="getIcon('apps')" class="connection-launcher__icon" />
            <span class="connection-launcher__label" aria-hidden="true">
              <span class="connection-launcher__label-top">{{ $gt('Zurück zu') }}</span>
              <span class="connection-launcher__label-main">{{ $gt('KARS-Connection') }}</span>
            </span>
          </button>
        </template>
      </v-tooltip>

      <!-- Aktive Organisation (Issue #326): permanent sichtbarer Kontext,
           damit der Nutzer immer sieht, fuer welches Unternehmen er gerade
           aktiv ist. Bei mehreren verknuepften Orgs ist die Chip ein
           Wechsel-Trigger. -->
      <div v-if="authStore.organizationName" class="bar-org" :title="orgChipTitle">
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <button v-bind="props" type="button" class="org-chip org-chip--switchable" :aria-label="orgChipTitle">
              <v-avatar size="22" class="org-chip__avatar">
                <v-img v-if="authStore.organizationLogo" :src="authStore.organizationLogo" cover alt="" />
                <component v-else :is="getIcon('companies')" :size="14" />
              </v-avatar>
              <span class="org-chip__name">{{ authStore.organizationName }}</span>
              <ChevronDown :size="14" class="org-chip__chevron" />
            </button>
          </template>
          <v-card rounded="lg" class="box-shadow-large">
            <v-list>
              <template v-if="showOrgSwitcher">
                <v-list-subheader class="org-switcher-header">{{ $gt('Unternehmen wechseln') }}</v-list-subheader>
                <ListItem
                  v-for="org in authStore.linkedOrgs"
                  :key="org.idOrganization"
                  :title="org.organizationName"
                  :iconType="org.isActive ? 'check' : 'companies'"
                  :disabled="org.isActive"
                  @click="!org.isActive && authStore.switchOrg(org.idOrganization)"
                />
                <v-divider></v-divider>
              </template>
              <ListItem :title="$gt('Unternehmen verknüpfen')" iconType="link" @click="$router.push({ name: 'PageWelcomeHub', query: { action: 'link' } })" />
              <ListItem :title="$gt('Neues Unternehmen erstellen')" iconType="plus" @click="$router.push({ name: 'PageWelcomeHub', query: { action: 'new' } })" />
              <v-divider></v-divider>
              <ListItem :title="$gt('Apps & Unternehmen')" iconType="apps" @click="$router.push({ name: 'PageWelcomeHub' })" />
            </v-list>
          </v-card>
        </v-menu>
      </div>

      <!-- Nav items (centered) -->
      <nav class="bar-nav" role="navigation">
        <template v-for="(item, index) in items" :key="index">
          <!-- Dropdown-Gruppe (z. B. Stammdaten = Kontakte + Fahrzeuge) -->
          <v-menu v-if="item.children" offset-y>
            <template v-slot:activator="{ props }">
              <button
                v-bind="props"
                type="button"
                class="nav-item"
                :class="{ 'nav-item--active': isGroupActive(item) }"
                :title="$gt(item.title)"
              >
                <component :is="getIcon(item.iconType)" class="nav-item-icon" />
                <span class="nav-item-title">{{ $gt(item.title) }}</span>
                <ChevronDown :size="16" class="nav-item-chevron" />
              </button>
            </template>
            <v-card rounded="lg" class="box-shadow-large">
              <v-list>
                <ListItem
                  v-for="child in item.children"
                  :key="child.title"
                  :title="$gt(child.title)"
                  :iconType="child.iconType"
                  @click="$router.push({ name: child.name, query: child.query })"
                />
              </v-list>
            </v-card>
          </v-menu>
          <!-- 26.07.26 VIII: "Fälle" als Split-Button — der kleine Pfeil
               öffnet die letzten 10 geöffneten Fälle (Direktabsprung ins
               Cockpit). -->
          <span v-else-if="item.recentCases" class="nav-item-split">
            <router-link
              :to="{ name: item.name, query: item.query }"
              class="nav-item nav-item--split-main"
              :class="{ 'nav-item--active': isActive(item.name) }"
              :title="$gt(item.title)"
            >
              <component :is="getIcon(item.iconType)" class="nav-item-icon" />
              <span class="nav-item-title">{{ $gt(item.title) }}</span>
            </router-link>
            <v-menu v-if="recentCases.length > 0" offset-y>
              <template v-slot:activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  class="nav-item nav-item--split-arrow"
                  :title="$gt('Zuletzt geöffnete Fälle')"
                  :aria-label="$gt('Zuletzt geöffnete Fälle')"
                  @click="validateRecentCases"
                >
                  <ChevronDown :size="15" class="nav-item-chevron" />
                </button>
              </template>
              <v-card rounded="lg" class="box-shadow-large">
                <v-list density="compact" class="pa-1" style="min-width: 240px">
                  <v-list-subheader>{{ $gt('Zuletzt geöffnet') }}</v-list-subheader>
                  <v-list-item
                    v-for="rc in recentCases"
                    :key="rc.id"
                    rounded="lg"
                    @click="openRecentCase(rc)"
                  >
                    <template v-slot:prepend>
                      <component :is="getIcon('cases')" class="list-item-icon" />
                    </template>
                    <v-list-item-title class="list-item-title">{{ rc.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </span>
          <!-- Einfacher Navigationslink -->
          <router-link
            v-else
            :to="{ name: item.name, query: item.query }"
            class="nav-item"
            :class="{ 'nav-item--active': isActive(item.name) }"
            :title="$gt(item.title)"
          >
            <component :is="getIcon(item.iconType)" class="nav-item-icon" />
            <span class="nav-item-title">{{ $gt(item.title) }}</span>
          </router-link>
        </template>
      </nav>

      <!-- User menu (right) -->
      <div class="bar-user">
        <HeaderUtilityCluster />
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <div v-bind="props" v-ripple class="d-flex align-center user-menu-button" style="cursor: pointer">
              <v-avatar size="40" class="mr-3" :color="avatarBgColor">
                <v-img v-if="authStore.userImage" :src="authStore.userImage" :alt="$gt('Profilbild')" cover />
                <span v-else style="font-weight: 500; color: #fff; font-size: 0.85rem">
                  {{ authStore.initials }}
                </span>
              </v-avatar>
              <!-- Eigene Klasse statt Vuetify-Utilities (d-flex etc. sind
                   !important und liessen sich sonst nicht per Media Query
                   ausblenden). -->
              <div class="text-truncate-wrapper">
                <span class="text-truncate user-name">{{ authStore.userName }}</span>
                <span class="text-truncate user-email">{{ authStore.email }}</span>
              </div>
              <ChevronDown class="chevron" />
            </div>
          </template>

          <v-card rounded="lg" class="box-shadow-large">
            <v-list>
              <ListItem :title="$gt('Apps & Unternehmen')" iconType="apps" @click="$router.push({ name: 'PageWelcomeHub' })" />
              <v-divider></v-divider>
              <!-- Reihenfolge laut Kundenfeedback 30.10.2026 (IV): Profil,
                   Allgemeine Einstellungen, Berechtigungen, Kalender, Downloads. -->
              <ListItem :title="$gt('Mein Profil')" iconType="userSettings" @click="handleProfileSettingsClick" />
              <!-- Einstellungen liegen zentral in KARS-Connection (/organisation);
                   die frühere eigene Seite /einstellungen entfällt (20.10.2026). -->
              <ListItem v-if="canManagePermissions" :title="$gt('Allgemeine Einstellungen')" iconType="settings" data-testid="nav-settings" @click="$router.push(organizationSettingsRoute)" />
              <!-- Unternehmenseinstellung, Abonnement und Rechnungen liegen
                   zentral in KARS-Connection (Willkommensseite). App-spezifische
                   Berechtigungen (Rollen) dagegen hier in KARS-Schaden. -->
              <ListItem v-if="canManagePermissions" :title="$gt('Berechtigungen')" iconType="shield" @click="$router.push({ name: 'PagePermissions' })" />
              <ListItem :title="$gt('Kalender-Einstellungen')" iconType="calendarSettings" @click="$router.push({ name: 'PageCalendarSettings' })" />
              <!-- Kundenmeeting 26.08.26: Downloads liegt nicht mehr unter
                   Stammdaten, sondern hier im Benutzermenü rechts. -->
              <ListItem :title="$gt('Downloads')" iconType="download" @click="$router.push({ name: 'PageDownloads' })" />
              <ListItem v-if="authStore.isSuperAdmin" :title="$gt('Super Admin')" iconType="admin" @click="$router.push({ name: 'AdminDashboard' })" />
              <ListItem :title="$gt('Hilfe')" iconType="help" @click="$router.push({ name: 'PageHelp' })" />
              <ListItem :title="$gt('Supportanfrage')" iconType="lifeBuoy" @click="handleSupportClick" />
              <template v-if="showOrgSwitcher">
                <v-divider></v-divider>
                <v-list-subheader class="org-switcher-header">{{ $gt('Unternehmen wechseln') }}</v-list-subheader>
                <ListItem
                  v-for="org in authStore.linkedOrgs"
                  :key="org.idOrganization"
                  :title="org.organizationName"
                  :iconType="org.isActive ? 'check' : 'companies'"
                  :disabled="org.isActive"
                  @click="!org.isActive && authStore.switchOrg(org.idOrganization)"
                />
                <ListItem
                  :title="$gt('Weiteres Unternehmen verknüpfen')"
                  iconType="add"
                  v-if="authStore.isAdministrator"
                  @click="$router.push({ name: 'PageOnboardingLinkOrg' })"
                />
              </template>
              <v-divider></v-divider>
              <ListItem :title="$gt('Abmelden')" iconType="logout" @click="authStore.logout()" />
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>
  </v-app-bar>

  <!-- Mobile/Tablet: app bar with hamburger + logo -->
  <v-app-bar v-else class="mobile-app-bar" flat app height="64">
    <div class="app-bar-content">
      <ButtonBase iconStyle iconType="menu" class="ml-2 mobile-menu-btn" @click="drawer = !drawer" />
      <v-spacer />
      <!-- Aktive Organisation auch im Mobile-Header anzeigen (Issue #326). -->
      <span v-if="authStore.organizationName" class="mobile-org-name" :title="orgChipTitle">
        {{ authStore.organizationName }}
      </span>
      <div class="mr-3">
        <NotifyCenterButton />
      </div>
      <v-img src="@/assets/images/logo_schaden_white.png" class="app-bar-logo mr-5" height="40" @click="goToHome" style="cursor: pointer" />
    </div>
  </v-app-bar>

  <!-- Mobile/Tablet: temporary navigation drawer -->
  <v-navigation-drawer
    v-if="!display.isDesktop"
    v-model="drawer"
    temporary
    app
    role="navigation"
  >
    <v-list nav>
      <!-- Absprung zur Connection-Plattform, analog zur Seitenleiste in
           KARS HR ganz oben im Drawer platziert. -->
      <v-list-item rounded="lg" :to="{ name: 'PageWelcomeHub' }" active-class="list-item-active">
        <template v-slot:prepend>
          <component :is="getIcon('apps')" class="list-item-icon" />
        </template>
        <v-list-item-title class="list-item-title">{{ $gt('Zu KARS-Connection') }}</v-list-item-title>
      </v-list-item>

      <v-divider class="my-2" />

      <template v-for="(item, index) in items" :key="index">
        <!-- Dropdown-Gruppe als Subheader + eingerückte Einträge -->
        <template v-if="item.children">
          <v-list-subheader class="mobile-group-header">{{ $gt(item.title) }}</v-list-subheader>
          <v-list-item
            v-for="child in item.children"
            :key="child.title"
            :to="{ name: child.name, query: child.query }"
            :color="isChildActive(child) ? 'primary' : undefined"
            active-class="list-item-active"
            rounded="lg"
          >
            <template v-slot:prepend>
              <component :is="getIcon(child.iconType)" :class="['list-item-icon', { 'list-item-icon--active': isChildActive(child) }]" />
            </template>
            <v-list-item-title :class="['list-item-title', { 'list-item-title--active': isChildActive(child) }]">
              {{ $gt(child.title) }}
            </v-list-item-title>
          </v-list-item>
        </template>
        <!-- Einfacher Navigationseintrag -->
        <v-list-item
          v-else
          :to="{ name: item.name, query: item.query }"
          :color="isActive(item.name) ? 'primary' : undefined"
          active-class="list-item-active"
          rounded="lg"
        >
          <template v-slot:prepend>
            <component :is="getIcon(item.iconType)" :class="['list-item-icon', { 'list-item-icon--active': isActive(item.name) }]" />
          </template>
          <v-list-item-title :class="['list-item-title', { 'list-item-title--active': isActive(item.name) }]">
            {{ $gt(item.title) }}
          </v-list-item-title>
        </v-list-item>
      </template>

      <v-list-item
        rounded="lg"
        :to="{ name: 'PageUserSettings' }"
        :color="isActive('PageUserSettings') ? 'primary' : undefined"
        active-class="list-item-active"
      >
        <template v-slot:prepend>
          <component :is="getIcon('userSettings')" :class="['list-item-icon', { 'list-item-icon--active': isActive('PageUserSettings') }]" />
        </template>
        <v-list-item-title :class="['list-item-title', { 'list-item-title--active': isActive('PageUserSettings') }]">
          {{ $gt('Mein Profil') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canManagePermissions"
        rounded="lg"
        :to="organizationSettingsRoute"
        :color="isActive('PageOrganization') ? 'primary' : undefined"
        active-class="list-item-active"
      >
        <template v-slot:prepend>
          <component :is="getIcon('settings')" :class="['list-item-icon', { 'list-item-icon--active': isActive('PageOrganization') }]" />
        </template>
        <v-list-item-title :class="['list-item-title', { 'list-item-title--active': isActive('PageOrganization') }]">
          {{ $gt('Allgemeine Einstellungen') }}
        </v-list-item-title>
      </v-list-item>

      <!-- Unternehmenseinstellung, Abonnement und Rechnungen liegen zentral
           in KARS-Connection (Willkommensseite). App-spezifische
           Berechtigungen (Rollen) dagegen hier in KARS-Schaden. -->
      <v-list-item
        v-if="canManagePermissions"
        rounded="lg"
        :to="{ name: 'PagePermissions' }"
        :color="isActive('PagePermissions') ? 'primary' : undefined"
        active-class="list-item-active"
      >
        <template v-slot:prepend>
          <component :is="getIcon('shield')" :class="['list-item-icon', { 'list-item-icon--active': isActive('PagePermissions') }]" />
        </template>
        <v-list-item-title :class="['list-item-title', { 'list-item-title--active': isActive('PagePermissions') }]">
          {{ $gt('Berechtigungen') }}
        </v-list-item-title>
      </v-list-item>

      <!-- Downloads: im Benutzerbereich, analog zum Desktop-Menü. -->
      <v-list-item
        rounded="lg"
        :to="{ name: 'PageDownloads' }"
        :color="isActive('PageDownloads') ? 'primary' : undefined"
        active-class="list-item-active"
      >
        <template v-slot:prepend>
          <component :is="getIcon('download')" :class="['list-item-icon', { 'list-item-icon--active': isActive('PageDownloads') }]" />
        </template>
        <v-list-item-title :class="['list-item-title', { 'list-item-title--active': isActive('PageDownloads') }]">
          {{ $gt('Downloads') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item v-if="authStore.isSuperAdmin" rounded="lg" :to="{ name: 'AdminDashboard' }">
        <template v-slot:prepend>
          <component :is="getIcon('admin')" class="list-item-icon" />
        </template>
        <v-list-item-title class="list-item-title">{{ $gt('Super Admin') }}</v-list-item-title>
      </v-list-item>

      <v-list-item rounded="lg" :to="{ name: 'PageHelp' }">
        <template v-slot:prepend>
          <component :is="getIcon('help')" class="list-item-icon" />
        </template>
        <v-list-item-title class="list-item-title">{{ $gt('Hilfe') }}</v-list-item-title>
      </v-list-item>

      <v-list-item rounded="lg" @click="handleSupportClick">
        <template v-slot:prepend>
          <component :is="getIcon('lifeBuoy')" class="list-item-icon" />
        </template>
        <v-list-item-title class="list-item-title">{{ $gt('Supportanfrage') }}</v-list-item-title>
      </v-list-item>

      <template v-if="showOrgSwitcher">
        <v-divider class="my-2" />
        <v-list-subheader class="org-switcher-header">{{ $gt('Unternehmen wechseln') }}</v-list-subheader>
        <v-list-item
          v-for="org in authStore.linkedOrgs"
          :key="'m-' + org.idOrganization"
          rounded="lg"
          :disabled="org.isActive"
          @click="!org.isActive && authStore.switchOrg(org.idOrganization)"
        >
          <template v-slot:prepend>
            <component :is="getIcon(org.isActive ? 'check' : 'companies')" class="list-item-icon" />
          </template>
          <v-list-item-title class="list-item-title">{{ org.organizationName }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="authStore.isAdministrator"
          rounded="lg"
          :to="{ name: 'PageOnboardingLinkOrg' }"
        >
          <template v-slot:prepend>
            <component :is="getIcon('add')" class="list-item-icon" />
          </template>
          <v-list-item-title class="list-item-title">{{ $gt('Weiteres Unternehmen verknüpfen') }}</v-list-item-title>
        </v-list-item>
      </template>

      <v-divider class="my-2" />

      <v-list-item @click="authStore.logout()">
        <template v-slot:prepend>
          <component :is="getIcon('logout')" class="list-item-icon" />
        </template>
        <v-list-item-title class="list-item-title">{{ $gt('Abmelden') }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import ButtonBase from "@/components/buttons/ButtonBase.vue";
import ListItem from "@/components/utils/ListItem.vue";
import HeaderUtilityCluster from "@/components/app/HeaderUtilityCluster.vue";
import NotifyCenterButton from "@/components/notifyCenter/NotifyCenterButton.vue";
import { ChevronDown } from "lucide-vue-next";

import { useUiStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore.ts";
import { useDisplayStore } from "@/stores/displayStore";
import { useRecentCasesStore } from "@/stores/recentCasesStore";
import { getIcon } from "@/components/utils/utils";
import { COMPANY_ROLE_VALUES, roleLabel } from "@/utils/organizationRoles";
import { gtKey } from "@/i18n/gt";

export default {
  components: {
    NotifyCenterButton,
    ButtonBase,
    ListItem,
    HeaderUtilityCluster,
    ChevronDown,
  },

  data() {
    return {
      drawer: false,
    };
  },

  created() {
    this.display = useDisplayStore();
    this.uiStore = useUiStore();
  },

  computed: {
    authStore() {
      return useAuthStore();
    },

    // Berechtigungen (Rollenverwaltung KARS-Schaden) sehen nur Rollen mit
    // Verwaltungsrechten; die Route erzwingt das zusaetzlich per meta.
    canManagePermissions() {
      return ["Administrator", "Unternehmensleiter", "Standortleiter"].includes(this.authStore.userRole);
    },

    // „Einstellungen" öffnet die Organisationsseite von KARS-Connection im
    // Hub-Fokus-Modus; sie ist nur für dieselben Rollen erreichbar.
    organizationSettingsRoute() {
      return { name: "PageOrganization", query: { from: "hub" } };
    },

    avatarBgColor() {
      const AVATAR_COLORS = ["#6b7280"];
      const id = Number(this.authStore.idUser) || 0;
      return AVATAR_COLORS[Math.abs(id) % AVATAR_COLORS.length];
    },

    showOrgSwitcher() {
      // Kundenreview 23.08.26, XXI: nicht auf Administratoren beschränken —
      // jeder mit mehr als einer Mitgliedschaft darf wechseln.
      const store = useAuthStore();
      return Array.isArray(store.linkedOrgs) && store.linkedOrgs.length > 1;
    },

    /** 26.07.26 VIII: die letzten 10 geöffneten Fälle (neueste zuerst). */
    recentCases() {
      return useRecentCasesStore().entries;
    },

    /**
     * Tooltip-Text fuer die Org-Chip in der TopBar (Issue #326).
     * Zeigt zusaetzlich zur Org auch die Rolle des Nutzers innerhalb dieser
     * Organisation, damit klar ist, in welchem Kontext man gerade arbeitet.
     */
    orgChipTitle() {
      const store = this.authStore;
      const name = store.organizationName || "";
      const roles = Array.isArray(store.organizationRoles)
        ? store.organizationRoles.filter(Boolean).map((r) => roleLabel(r)).join(", ")
        : "";
      if (!name) return "";
      const base = `Aktive Organisation: ${name}`;
      const withRoles = roles ? `${base} (${roles})` : base;
      return this.showOrgSwitcher ? `${withRoles} – klicken zum Wechseln` : withRoles;
    },

    items() {
      const allItems = [
        {
          title: gtKey("Dashboard"),
          iconType: "start",
          name: "PageDashboard",
        },
        {
          title: gtKey("Fälle"),
          iconType: "cases",
          name: "PageCases",
          // 26.07.26 VIII: Pfeil mit den letzten 10 geöffneten Fällen.
          recentCases: true,
        },
        // Kundenfeedback Kanzlei Polz 27.10.2026 (III): Der Kalender hängt
        // unter „Wiedervorlagen“ statt als eigener Punkt – die Leiste ist voll,
        // und Wiedervorlagen/Fristen stehen ohnehin im Kalender.
        {
          title: gtKey("Wiedervorlagen"),
          iconType: "reminders",
          allowedOrganizationRoles: COMPANY_ROLE_VALUES,
          children: [
            {
              title: gtKey("Wiedervorlagen"),
              iconType: "reminders",
              name: "PageReminders",
            },
            {
              title: gtKey("Kalender"),
              iconType: "calendarDays",
              name: "PageCalendar",
            },
          ],
        },
        // Meeting 27.07.26: Reihenfolge Dashboard, Fälle, Wiedervorlagen,
        // Korrespondenz, Stammdaten, danach der Rest. "Kommunikation" heißt
        // jetzt sichtbar "Korrespondenz" (Routen-Namen bleiben unverändert).
        {
          title: gtKey("Korrespondenz"),
          iconType: "messages",
          children: [
            {
              title: gtKey("Korrespondenz"),
              iconType: "messages",
              name: "PageCommunication",
            },
            // Der zentrale Posteingang (Seite /posteingang) ist vorerst
            // ausgeblendet (Kundenfeedback 13.09.26): Archiv-Mails und
            // Dokumenteneingänge erscheinen unter Korrespondenz › E-Mails –
            // global, solange sie keinem Fall zugeordnet sind, danach im Fall.
            // Zum Reaktivieren: Eintrag { title: "Posteingang", iconType: "inbox",
            // name: "PageInbox" } und die Route in router.ts wiederherstellen.
            {
              title: gtKey("Korrespondenzeinstellungen"),
              iconType: "coreData",
              name: "PageCommunicationSettings",
              allowedOrganizationRoles: COMPANY_ROLE_VALUES,
              allowedUserRoles: ["Administrator", "Unternehmensleiter", "Standortleiter"],
            },
          ],
        },
        {
          title: gtKey("Stammdaten"),
          iconType: "coreData",
          allowedOrganizationRoles: COMPANY_ROLE_VALUES,
          children: [
            {
              title: gtKey("Kontakte"),
              iconType: "addresses",
              name: "PageContacts",
            },
            {
              title: gtKey("Fahrzeuge"),
              iconType: "carFront",
              name: "PageVehiclesMaster",
            },
            // Kundenfeedback 25.10.2026 Teil 2, VII / 26.10.2026, IX:
            // zentrale Bibliothek der Versicherungsbedingungen (AKB, Kasko
            // und Kfz-Haftpflicht) – Stammdaten wie Kontakte und Fahrzeuge.
            {
              title: gtKey("Versicherungsbedingungen"),
              iconType: "insurance",
              name: "PageKaskoConditions",
            },
          ],
        },
        // Privatkunde/Zeuge: eigene Stammdaten. Der Downloadbereich liegt
        // seit dem Kundenmeeting 26.08.26 im Benutzermenü rechts.
        {
          title: gtKey("Stammdaten"),
          iconType: "coreData",
          allowedOrganizationRoles: ["Privatkunde", "Zeuge"],
          name: "PageCoreData",
        },
        {
          title: gtKey("KARS entdecken"),
          iconType: "features",
          children: [
            {
              title: gtKey("Features"),
              iconType: "features",
              name: "PageFeatures",
              query: { view: "features" },
            },
            {
              title: gtKey("Add-ins"),
              iconType: "addins",
              name: "PageFeatures",
              query: { view: "addins" },
            },
          ],
        },
      ];

      const organizationRoles = this.authStore.organizationRoles ?? [];
      const userRole = this.authStore.userRole ?? null;

      // Light-User dürfen keine globalen Navigationsziele sehen, sie sind
      // an einen einzigen Fall gebunden und werden vom Router-Guard dorthin
      // geleitet.
      if (this.authStore.isLightUser) {
        return [];
      }

      return allItems.filter((item) => {
        const organizationRoleMatch = !item.allowedOrganizationRoles || item.allowedOrganizationRoles.some((role) => organizationRoles.includes(role));
        const userRoleMatch = !item.allowedUserRoles || item.allowedUserRoles.includes(userRole);
        return organizationRoleMatch && userRoleMatch;
      }).map((item) => {
        // Dropdown-Kinder ebenfalls nach Rollen filtern (z. B.
        // "Kommunikationseinstellungen" nur für Administrator/Standortleiter).
        if (!Array.isArray(item.children)) return item;
        const children = item.children.filter((child) => {
          const childOrgMatch = !child.allowedOrganizationRoles || child.allowedOrganizationRoles.some((role) => organizationRoles.includes(role));
          const childUserMatch = !child.allowedUserRoles || child.allowedUserRoles.includes(userRole);
          return childOrgMatch && childUserMatch;
        });
        // Bleibt nur ein Kind übrig, als einfachen Link statt Dropdown anzeigen.
        if (children.length === 1) {
          const [only] = children;
          return { title: item.title, iconType: item.iconType, name: only.name, query: only.query };
        }
        return { ...item, children };
      }).filter((item) => !Array.isArray(item.children) || item.children.length > 0);
    },
  },

  methods: {
    getIcon(iconType) {
      return getIcon(null, iconType);
    },

    /**
     * Mail RA Polz (09/2026): Beim Öffnen des Menüs die Liste gegen den Server
     * abgleichen. Fälle, die eine Kollegin oder ein anderes Gerät inzwischen
     * gelöscht hat, verschwinden damit noch in derselben Sitzung — ohne den
     * Abgleich blieben sie bis zum nächsten Anmelden stehen.
     */
    validateRecentCases() {
      useRecentCasesStore().validate();
    },

    /** 26.07.26 VIII: zuletzt geöffneten Fall direkt im Cockpit öffnen. */
    openRecentCase(entry) {
      if (!entry?.id) return;
      // PageCaseDetail erwartet die Klartext-ID (wie alle anderen Absprünge,
      // z. B. aus der Fall-Liste). Mit verschlüsselter ID liefen alle
      // Cockpit-Requests auf einen nicht existierenden Fall → 403-Toast
      // "keine Berechtigung" + leeres Cockpit.
      this.$router.push({
        name: "PageCaseDetail",
        params: { idCase: String(entry.id) },
      });
    },

    isActive(name) {
      return this.$route.name === name;
    },

    isChildActive(child) {
      if (this.$route.name !== child.name) return false;
      if (!child.query) return true;
      return Object.entries(child.query).every(([key, value]) => String(this.$route.query?.[key] ?? "") === String(value));
    },

    isGroupActive(item) {
      if (!Array.isArray(item.children)) return false;
      return item.children.some((child) => this.isActive(child.name));
    },

    goToHome() {
      // Feedback VI: Das KARS-Schaden-Logo fuehrt zur Startseite der
      // Fachanwendung. Zur Connection-Plattform geht es ueber den
      // App-Launcher links vom Logo sowie das Nutzer-/Organisationsmenue.
      this.$router.push({ name: "PageDashboard" });
    },

    goToConnection() {
      this.$router.push({ name: "PageWelcomeHub" });
    },

    handleProfileSettingsClick() {
      this.$router.push({ name: "PageUserSettings" });
    },

    handleSupportClick() {
      this.uiStore.openSupportDialog();
    },

  },
};
</script>

<style scoped>
/* ── Desktop app bar ─────────────────────────────────── */
.desktop-app-bar {
  background-color: #1a1b2e !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.bar-layout {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 20px;
  position: relative;
}

.bar-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.bar-notify {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-left: 14px;
}

/* App-Launcher: kompakter Absprung zur Connection-Plattform. */
.connection-launcher {
  appearance: none;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.75);
  border-radius: 8px;
  padding: 6px 10px 6px 6px;
  /* Steht rechts neben dem Logo, daher Abstand nach links. */
  margin-left: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}
.connection-launcher:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}
.connection-launcher__icon {
  width: 20px;
  height: 20px;
  stroke-width: 1.5;
  flex-shrink: 0;
}
.connection-launcher__label {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
  text-align: left;
}
.connection-launcher__label-top {
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}
.connection-launcher__label-main {
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
}
.connection-launcher:hover .connection-launcher__label-main {
  color: #ffffff;
}

/* Aktive Organisation in TopBar (Issue #326). */
.bar-org {
  flex-shrink: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  margin-left: 14px;
  /* Chip darf nie unter die Nachbarelemente laufen, wenn der Platz knapp wird. */
  overflow: hidden;
}
.org-chip {
  appearance: none;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.95);
  border-radius: 999px;
  padding: 4px 10px 4px 4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.2;
  max-width: 240px;
  /* Erlaubt dem Chip, mit dem schrumpfenden .bar-org mitzugehen; der Name
     truncated dann per Ellipsis statt ueberzulaufen. */
  min-width: 0;
  white-space: nowrap;
  cursor: default;
}
.org-chip--switchable {
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}
.org-chip--switchable:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.24);
}
.org-chip__avatar {
  background: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
}
.org-chip__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.org-chip__chevron {
  flex-shrink: 0;
  opacity: 0.75;
}

.mobile-org-name {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.85rem;
  font-weight: 500;
  margin-right: 12px;
  max-width: 45vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Frueher absolut zentriert (left: 50%), was bei schmalen Fenstern mit
   Org-Chip und Nutzerbereich ueberlappte. Als normales Flex-Kind mit
   Auto-Margins bleibt die Nav mittig, kann aber nie ueberlappen. */
.bar-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin: 0 auto;
  padding: 0 12px;
}

.bar-user {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* Nav items */
.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.85);
  transition: background-color 0.15s, color 0.15s;
}

/* Dropdown-Trigger nutzt ein <button>, daher Browser-Defaults zuruecksetzen */
button.nav-item {
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

/* 26.07.26 VIII: "Fälle"-Split-Button — Link + schmaler Pfeil daneben. */
.nav-item-split {
  display: inline-flex;
  align-items: stretch;
}
.nav-item--split-main {
  border-radius: 8px 0 0 8px;
}
.nav-item--split-arrow {
  padding: 6px 5px;
  border-radius: 0 8px 8px 0;
  margin-left: -4px;
}

.nav-item-chevron {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.15s;
}

.nav-item:hover .nav-item-chevron,
.nav-item--active .nav-item-chevron {
  color: #ffffff;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.nav-item--active {
  color: #ffffff;
  background-color: rgba(79, 154, 255, 0.22);
  box-shadow: 0 0 0 1px rgba(79, 154, 255, 0.35), 0 2px 10px rgba(0, 100, 255, 0.12);
}

.nav-item-icon {
  width: 18px;
  height: 18px;
  stroke-width: 1.25;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
}

.nav-item:hover .nav-item-icon {
  color: #ffffff;
}

.nav-item--active .nav-item-icon {
  stroke-width: 1.75;
  color: #93c5fd;
}

.nav-item-title {
  font-size: 0.875rem;
  font-weight: 400;
  white-space: nowrap;
}

.nav-item--active .nav-item-title {
  font-weight: 600;
  color: #ffffff;
}

/* User menu */
.user-menu-button {
  border-radius: 8px;
  padding: 6px 8px;
}

.user-menu-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.text-truncate-wrapper {
  display: flex;
  flex-direction: column;
  margin-right: 8px;
  max-width: 200px;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

.user-email {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
}

.chevron {
  width: 20px;
  height: 20px;
  stroke-width: 1.5;
  color: rgba(255, 255, 255, 0.7);
}

/* ── Responsive Stufen der Desktop-Leiste (≥ 960px) ───────
   Die Leiste gibt bei sinkender Breite schrittweise Platz frei,
   statt Elemente ueberlappen zu lassen:
   1. ≤ 1660px: E-Mail unter dem Nutzernamen ausblenden
   2. ≤ 1520px: Nutzertext komplett weg (nur Avatar), Org-Chip schmaler
   3. ≤ 1400px: Nav-Items nur als Icons (Tooltip zeigt den Titel)
   4. ≤ 1100px: Org-Chip auf Minimum */
@media (max-width: 1660px) {
  .user-email {
    display: none;
  }
}

@media (max-width: 1520px) {
  .text-truncate-wrapper {
    display: none;
  }
  .org-chip {
    max-width: 180px;
  }
}

@media (max-width: 1400px) {
  .nav-item-title {
    display: none;
  }
  .nav-item {
    padding: 8px 10px;
  }
  .nav-item-icon {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 1100px) {
  .org-chip {
    max-width: 120px;
  }
  .bar-nav {
    padding: 0 6px;
  }
  /* Launcher-Beschriftung weicht dem Platz; Icon + Tooltip bleiben. */
  .connection-launcher__label {
    display: none;
  }
  .connection-launcher {
    padding: 6px;
  }
}

/* ── Mobile app bar ──────────────────────────────────── */
.mobile-app-bar {
  background-color: #1a1b2e !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.app-bar-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.app-bar-logo {
  max-width: 120px;
}

.mobile-menu-btn {
  color: rgba(255, 255, 255, 0.85) !important;
}

/* ── Mobile drawer list items ────────────────────────── */
.list-item-title {
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--dark-color);
}

.list-item-title--active {
  font-weight: 600;
  color: var(--primary-color);
}

.list-item-icon {
  width: 24px;
  height: 24px;
  stroke-width: 1.25;
  margin-right: 12px;
  color: var(--dark-color);
}

.list-item-icon--active {
  stroke-width: 1.5;
  color: var(--primary-color);
}
</style>
